import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbSearch, TbX } from 'react-icons/tb';

import useStore from '../../store';
import HistoryMasonry from './HistoryMasonry';
import './HistoryGallery.css';

function HistoryCard({ item, onOpen }) {
  const { t, i18n } = useTranslation();
  const [loaded, setLoaded] = useState(false);

  const createdAt = item?.createdAt
    ? new Intl.DateTimeFormat(i18n.language || 'ru', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(item.createdAt))
    : '';

  const previewRatio = item?.preview?.width && item?.preview?.height
    ? (item.preview.height / item.preview.width) * 100
    : 112;

  const fallbackText = item?.previewStatus === 'mirror' && item?.previewBotUsername
    ? ((i18n.language || '').startsWith('ru')
      ? `\u0417\u0435\u0440\u043a\u0430\u043b\u043e ${item.previewBotUsername}`
      : `Mirror ${item.previewBotUsername}`)
    : t('art_history.missing_preview');

  return (
    <button
      className="history-card"
      type="button"
      onClick={() => onOpen(item)}
    >
      <div
        className="history-card-media"
        style={{ paddingBottom: `${previewRatio}%` }}
      >
        {!loaded && <div className="history-card-skeleton" />}
        {item?.preview?.src ? (
          <img
            src={item.preview.src}
            alt={item.title || t('art_history.untitled')}
            loading="lazy"
            onLoad={() => setLoaded(true)}
            className={loaded ? 'history-card-image is-loaded' : 'history-card-image'}
          />
        ) : (
          <div className="history-card-fallback">
            <span>{fallbackText}</span>
          </div>
        )}
      </div>
      <div className="history-card-meta">
        <div className="history-card-title">
          {item.title || t('art_history.untitled')}
        </div>
        <div className="history-card-date">{createdAt}</div>
      </div>
    </button>
  );
}

function buildGroupedTitle({ isRu, historyUserName, historyGroupTitle }) {
  const safeUserName = String(historyUserName || '').trim();
  const safeGroupTitle = String(historyGroupTitle || '').trim();

  if (safeUserName && safeGroupTitle) {
    return isRu
      ? `\u0420\u0438\u0441\u0443\u043d\u043a\u0438 ${safeUserName} \u0432 ${safeGroupTitle}`
      : `${safeUserName}'s drawings in ${safeGroupTitle}`;
  }

  if (safeGroupTitle) {
    return isRu
      ? `\u0420\u0438\u0441\u0443\u043d\u043a\u0438 \u0432 ${safeGroupTitle}`
      : `Drawings in ${safeGroupTitle}`;
  }

  return isRu
    ? '\u0420\u0438\u0441\u0443\u043d\u043a\u0438 \u0432 \u0433\u0440\u0443\u043f\u043f\u0435'
    : 'Drawings in group';
}

function getHistoryErrorText({ loadError, isRu, t }) {
  if (loadError === 'notGroupMember') {
    return isRu
      ? '\u042d\u0442\u043e\u0442 \u0441\u043f\u0438\u0441\u043e\u043a \u0434\u043e\u0441\u0442\u0443\u043f\u0435\u043d \u0442\u043e\u043b\u044c\u043a\u043e \u0443\u0447\u0430\u0441\u0442\u043d\u0438\u043a\u0430\u043c \u044d\u0442\u043e\u0439 \u0433\u0440\u0443\u043f\u043f\u044b.'
      : 'This gallery is available only to members of this group.';
  }

  return t('art_history.loading_error');
}

function HistoryGallery() {
  const { t, i18n } = useTranslation();
  const socket = useStore(state => state.socket);
  const socketStatus = useStore(state => state.socketStatus);
  const isValid = useStore(state => state.isValid);
  const telegram = useStore(state => state.ref.telegram);
  const navigateToStartParam = useStore(state => state.navigateToStartParam);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(-1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [historyMeta, setHistoryMeta] = useState(null);

  const sentinelRef = useRef(null);
  const mountedRef = useRef(true);
  const loadingRef = useRef(false);
  const searchInputRef = useRef(null);
  const requestIdRef = useRef(0);

  const {
    startParam,
    socketName,
    historyScope,
    historyTargetUserId,
    historyGroupKey,
    historyIsOwnGroupView,
  } = telegram;

  const isRu = (i18n.language || '').startsWith('ru');
  const isGroupedHistory = historyScope === 'group' && historyGroupKey;
  const filterOptions = [
    { value: 'all', label: isRu ? '\u0412\u0441\u0435' : 'All' },
    { value: 'strokes300', label: isRu ? '>300 \u0448\u0442\u0440' : '>300 strokes' },
    { value: 'strokes1000', label: isRu ? '>1000 \u0448\u0442\u0440' : '>1000 strokes' },
    { value: 'auction', label: isRu ? '\u041d\u0430 \u0430\u0443\u043a\u0446\u0438\u043e\u043d\u0435' : 'On auction' },
  ];

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      loadingRef.current = false;
    };
  }, []);

  useEffect(() => {
    const debounceId = setTimeout(() => {
      if (!mountedRef.current) { return; }
      setSearchQuery(searchInput.trim());
    }, searchInput ? 260 : 0);

    return () => clearTimeout(debounceId);
  }, [searchInput]);

  useEffect(() => {
    if (!isSearchVisible) {
      return;
    }

    const focusId = setTimeout(() => {
      searchInputRef.current?.focus?.();
    }, 140);

    return () => clearTimeout(focusId);
  }, [isSearchVisible]);

  const requestPage = useCallback((nextPage, { reset = false } = {}) => {
    if (!socket || socketStatus !== 'connected' || !isValid || loadingRef.current) {
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    loadingRef.current = true;
    setIsLoading(true);
    setLoadError('');

    socket.emit('myDrawings', {
      page: nextPage,
      pageSize: 24,
      search: searchQuery,
      filter: activeFilter,
      targetUserId: isGroupedHistory ? historyTargetUserId : undefined,
      groupKey: isGroupedHistory ? historyGroupKey : undefined,
    }, (result = {}) => {
      if (!mountedRef.current || currentRequestId !== requestIdRef.current) { return; }

      if (result?.error) {
        loadingRef.current = false;
        setLoadError(result.error);
        setIsLoading(false);
        setInitialLoaded(true);
        return;
      }

      const nextItems = Array.isArray(result.items) ? result.items : [];

      if (result?.meta) {
        setHistoryMeta(result.meta);
      }

      setItems(prevItems => {
        const merged = reset ? nextItems : [...prevItems, ...nextItems];
        const unique = new Map();

        merged.forEach(item => {
          if (item?.id) {
            unique.set(item.id, item);
          }
        });

        return Array.from(unique.values());
      });

      setPage(typeof result.page === 'number' ? result.page : nextPage);
      setHasMore(Boolean(result.hasMore));
      loadingRef.current = false;
      setIsLoading(false);
      setInitialLoaded(true);
    });
  }, [
    socket,
    socketStatus,
    isValid,
    searchQuery,
    activeFilter,
    isGroupedHistory,
    historyTargetUserId,
    historyGroupKey,
  ]);

  useEffect(() => {
    if (socketStatus === 'connected' && isValid && !initialLoaded && !isLoading) {
      requestPage(0, { reset: true });
    }
  }, [socketStatus, isValid, initialLoaded, isLoading, requestPage]);

  useEffect(() => {
    requestIdRef.current += 1;
    loadingRef.current = false;
    setItems([]);
    setPage(-1);
    setHasMore(true);
    setIsLoading(false);
    setLoadError('');
    setInitialLoaded(false);
    setHistoryMeta(null);
  }, [searchQuery, activeFilter, historyScope, historyTargetUserId, historyGroupKey]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || isLoading || !initialLoaded) {
      return;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        requestPage(page + 1);
      }
    }, {
      rootMargin: '320px',
    });

    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [page, hasMore, isLoading, initialLoaded, socketStatus, isValid, requestPage]);

  const openDrawing = (item) => {
    if (!item?.startParam) { return; }

    navigateToStartParam(item.startParam, {
      historyBackParam: startParam,
    });
  };

  const openAllHistory = () => {
    if (!socketName) { return; }
    navigateToStartParam(`history--${socketName}`);
  };

  const subtitleText = (i18n.language || '').startsWith('ru')
    ? '\u041d\u0430\u0436\u043c\u0438\u0442\u0435 \u043d\u0430 \u0440\u0438\u0441\u0443\u043d\u043e\u043a, \u0447\u0442\u043e\u0431\u044b \u043e\u0442\u043a\u0440\u044b\u0442\u044c \u0445\u043e\u043b\u0441\u0442'
    : t('art_history.subtitle');

  const titleText = isGroupedHistory
    ? buildGroupedTitle({
      isRu,
      historyUserName: historyMeta?.userName,
      historyGroupTitle: historyMeta?.groupTitle,
    })
    : t('art_history.title');

  const retryInitial = () => {
    loadingRef.current = false;
    setItems([]);
    setPage(-1);
    setHasMore(true);
    setLoadError('');
    setInitialLoaded(false);
  };

  const toggleSearch = () => {
    if (isSearchVisible) {
      setSearchInput('');
      setSearchQuery('');
      setIsSearchVisible(false);
      return;
    }

    setIsSearchVisible(true);
  };

  const loadErrorText = getHistoryErrorText({ loadError, isRu, t });
  const showConnecting = !initialLoaded && (items.length === 0 || !isValid || socketStatus !== 'connected' || isLoading);
  const showEmpty = initialLoaded && !isLoading && !loadError && items.length === 0;
  const showAllLabel = isRu ? '\u041f\u043e\u043a\u0430\u0437\u0430\u0442\u044c \u0432\u0441\u0435' : 'Show all';

  return (
    <div className="history-gallery touch-active add-scroll">
      <div className="history-gallery-shell">
        <div className="history-gallery-header">
          <div className="history-gallery-title-row">
            <div className="history-gallery-title-block">
              <h1 className="history-gallery-title">{titleText}</h1>
              <p className="history-gallery-subtitle">{subtitleText}</p>
            </div>

            {historyIsOwnGroupView && (
              <button
                type="button"
                className="history-gallery-show-all"
                onClick={openAllHistory}
              >
                {showAllLabel}
              </button>
            )}
          </div>

          <div className={isSearchVisible ? 'history-gallery-search is-visible' : 'history-gallery-search'}>
            <div className="history-gallery-search-shell">
              <TbSearch className="history-gallery-search-icon" />
              <input
                ref={searchInputRef}
                className="history-gallery-search-input touch-active"
                type="text"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder={isRu ? '\u041f\u043e\u0438\u0441\u043a \u043f\u043e \u0440\u0438\u0441\u0443\u043d\u043a\u0430\u043c' : 'Search drawings'}
              />
              {searchInput && (
                <button
                  type="button"
                  className="history-gallery-search-clear"
                  onClick={() => {
                    setSearchInput('');
                    setSearchQuery('');
                  }}
                >
                  <TbX />
                </button>
              )}
            </div>
          </div>

          <div className="history-gallery-tabs-row">
            <div className="history-gallery-filters touch-active">
              {filterOptions.map(option => (
                <button
                  key={option.value}
                  type="button"
                  className={activeFilter === option.value ? 'history-gallery-filter is-active' : 'history-gallery-filter'}
                  onClick={() => setActiveFilter(option.value)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={isSearchVisible || searchQuery ? 'history-gallery-search-toggle is-active' : 'history-gallery-search-toggle'}
              onClick={toggleSearch}
            >
              <TbSearch />
            </button>
          </div>
        </div>

        {showConnecting && (
          <div className="history-gallery-state">
            <div className="history-gallery-spinner" />
            <div>{t('art_history.loading')}</div>
          </div>
        )}

        {!showConnecting && loadError && items.length === 0 && (
          <div className="history-gallery-state">
            <div>{loadErrorText}</div>
            {loadError !== 'notGroupMember' && (
              <button
                type="button"
                className="history-gallery-retry"
                onClick={retryInitial}
              >
                {t('art_history.retry')}
              </button>
            )}
          </div>
        )}

        {showEmpty && (
          <div className="history-gallery-state">
            <div>{t('art_history.empty')}</div>
          </div>
        )}

        {items.length > 0 && (
          <>
            <HistoryMasonry
              items={items}
              gap={6}
              minColumnWidth={122}
              minColumns={3}
              maxColumns={5}
              renderItem={item => (
                <HistoryCard
                  key={item.id}
                  item={item}
                  onOpen={openDrawing}
                />
              )}
            />

            {loadError && (
              <div className="history-gallery-footer">
                <span>{loadErrorText}</span>
                {loadError !== 'notGroupMember' && (
                  <button
                    type="button"
                    className="history-gallery-retry"
                    onClick={() => requestPage(page + 1)}
                  >
                    {t('art_history.retry')}
                  </button>
                )}
              </div>
            )}

            {isLoading && (
              <div className="history-gallery-footer">
                <div className="history-gallery-spinner" />
              </div>
            )}

            <div ref={sentinelRef} className="history-gallery-sentinel" />
          </>
        )}
      </div>
    </div>
  );
}

export default HistoryGallery;
